import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Button, Input, Panel, SegmentedControl } from '@usacow/ds';
import { DRILL_KINDS, DRILL_LABEL, type DrillKind } from '@usacow/drills';
import { api, type ClassRow, type RosterEntry } from '../../lib/api';

const SKILL_LABEL: Record<string, string> = {
  parsing: 'Parsing',
  modeling: 'Modeling',
  algorithm_choice: 'Algorithm choice',
  execution: 'Execution',
  proof: 'Proof',
  implementation: 'Implementation',
  retention: 'Retention',
};

export function ClassDetail() {
  const { id = '' } = useParams();
  const [cls, setCls] = useState<ClassRow | null>(null);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [cohorts, setCohorts] = useState<{ skill: string; students: string[] }[]>([]);
  const [tab, setTab] = useState('roster');

  // Assignment builder
  const [title, setTitle] = useState('');
  const [drill, setDrill] = useState<DrillKind>('constraint_sniffer');
  const [assigned, setAssigned] = useState(false);

  async function load() {
    const [r, ch] = await Promise.all([api.coachRoster(id), api.coachCohorts(id)]);
    setCls(r.class);
    setRoster(r.roster);
    setCohorts(ch.cohorts);
  }
  useEffect(() => {
    void load();
  }, [id]);

  async function assign() {
    if (!title.trim()) return;
    await api
      .coachCreateAssignment(id, { title: title.trim(), kind: drill, targetRef: drill })
      .catch(() => {});
    setTitle('');
    setAssigned(true);
    setTimeout(() => setAssigned(false), 2500);
  }

  if (!cls) return <div className="dim">Loading…</div>;

  return (
    <div className="scr">
      <Link to="/coach" className="lib-back">
        ← Classes
      </Link>
      <div className="scr-head">
        <div>
          <div className="t-eyebrow">class · join code {cls.join_code}</div>
          <h1 className="scr-h">{cls.name}</h1>
        </div>
        <SegmentedControl
          subtle
          value={tab}
          onChange={setTab}
          options={[
            { value: 'roster', label: 'Roster' },
            { value: 'assign', label: 'Assign' },
            { value: 'cohorts', label: 'Cohorts' },
          ]}
        />
      </div>

      {tab === 'roster' && (
        <Panel title="Roster" meta="who needs attention first">
          {roster.length === 0 ? (
            <p className="dim">
              No students yet. Share the join code: <b className="mono">{cls.join_code}</b>
            </p>
          ) : (
            <table className="coach-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Readiness</th>
                  <th>Bottleneck</th>
                  <th>Attempts</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((s) => (
                  <tr key={s.user_id}>
                    <td>
                      <Link to={`/coach/student/${s.user_id}`}>{s.name ?? s.email}</Link>
                    </td>
                    <td className="mono">{s.readiness}</td>
                    <td>{s.bottleneck ? (SKILL_LABEL[s.bottleneck] ?? s.bottleneck) : '—'}</td>
                    <td className="mono">{s.attempts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {tab === 'assign' && (
        <Panel title="Assignment builder">
          <div className="coach-assign">
            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Silver static-query recognition"
            />
            <div>
              <div className="uc-field__label" style={{ marginBottom: 5 }}>
                Drill
              </div>
              <select
                className="uc-input"
                value={drill}
                onChange={(e) => setDrill(e.target.value as DrillKind)}
              >
                {DRILL_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {DRILL_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
            <Button variant="primary" onClick={assign}>
              Assign to class
            </Button>
          </div>
          {assigned && <p className="coach-assigned">Assigned to {roster.length} student(s).</p>}
        </Panel>
      )}

      {tab === 'cohorts' && (
        <Panel title="Cohorts by shared weakness">
          {cohorts.length === 0 ? (
            <p className="dim">No assessed students yet.</p>
          ) : (
            <div className="coach-cohorts">
              {cohorts.map((co) => (
                <div className="coach-cohort" key={co.skill}>
                  <Badge tone="warning" variant="outline">
                    {SKILL_LABEL[co.skill] ?? co.skill}
                  </Badge>
                  <span className="dim">{co.students.join(', ')}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
